import { useEffect, useMemo, useState } from "react";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { renderDocumentPdfToBlob } from "@/lib/document-builder/pdf/renderDocumentPdfToBlob";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import { downloadBlobAsFile, uploadDocExportPdf } from "@/lib/document/docExportClient";
import { DocumentBlockReadOnlyList } from "./DocumentBlockReadOnlyList";

type Props = {
  open: boolean;
  documentId: string;
  versionId: string;
  documentName: string;
  versionNumber: number;
  blocks: DocBlock[];
  assets: DocAsset[];
  filledValues: DocVariableValues;
  onClose: () => void;
  onExportRegistered?: () => void;
  /** Skip published-version server gate (editor draft preview). */
  skipPreviewGate?: boolean;
  /** Hide Save export — e.g. editor preview of unsaved draft. */
  allowRegisterExport?: boolean;
  previewTitle?: string;
  previewHint?: string;
};

export function DocumentExportPreviewModal(props: Props) {
  const {
    open,
    documentId,
    versionId,
    documentName,
    versionNumber,
    blocks,
    assets,
    filledValues,
    onClose,
    onExportRegistered,
    skipPreviewGate = false,
    allowRegisterExport = true,
    previewTitle = "Export preview",
    previewHint,
  } = props;

  const org = useOrgSettings();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [rendering, setRendering] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredExportId, setRegisteredExportId] = useState<string | null>(null);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);

  const pdfUrl = useMemo(() => (pdfBlob ? URL.createObjectURL(pdfBlob) : null), [pdfBlob]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!open) {
      setPdfBlob(null);
      setError(null);
      setRegisteredExportId(null);
      setShowHtmlPreview(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setRendering(true);
      setError(null);
      const r = await renderDocumentPdfToBlob({
        documentId,
        versionId,
        documentTitle: documentName,
        versionNumber,
        brandName: org.brandName || "Document",
        tagline: org.tagline,
        logoUrl: org.logoUrl,
        blocks,
        assets,
        variableValues: filledValues,
        skipPreviewGate,
      });
      if (cancelled) return;
      setRendering(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setPdfBlob(r.blob);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    documentId,
    versionId,
    documentName,
    versionNumber,
    blocks,
    assets,
    filledValues,
    org.brandName,
    org.tagline,
    org.logoUrl,
    skipPreviewGate,
  ]);

  if (!open) return null;

  async function handleDownload() {
    if (!pdfBlob) return;
    const safeName = documentName.replace(/[<>:"/\\|?*]+/g, "_").trim() || "document";
    downloadBlobAsFile(pdfBlob, `${safeName}_v${versionNumber}.pdf`);
  }

  async function handleRegister() {
    if (!pdfBlob) return;
    setRegistering(true);
    setError(null);
    const r = await uploadDocExportPdf({
      documentId,
      versionId,
      variableValues: filledValues,
      blob: pdfBlob,
    });
    setRegistering(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setRegisteredExportId(r.export.id);
    onExportRegistered?.();
  }

  function handlePrint() {
    if (!pdfUrl) return;
    const printWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");
    if (!printWindow) return;
    printWindow.addEventListener("load", () => {
      printWindow.focus();
      printWindow.print();
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60 px-4 py-6 sm:px-6">
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <header className="flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-5 py-3 dark:border-zinc-700">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">{previewTitle}</h3>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-zinc-400">
              {documentName} — v{versionNumber}
            </p>
            {previewHint ? (
              <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">{previewHint}</p>
            ) : null}
            {registeredExportId ? (
              <p className="mt-1 text-xs text-green-700 dark:text-green-300">
                Registered export {registeredExportId.slice(0, 8)}…
              </p>
            ) : null}
            {error ? <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p> : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowHtmlPreview((v) => !v)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
            >
              {showHtmlPreview ? "PDF view" : "HTML view"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={registering}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => void handlePrint()}
              disabled={!pdfBlob || rendering}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={!pdfBlob || rendering}
              className="rounded-lg border border-indigo-300 px-3 py-1.5 text-sm text-indigo-700 dark:border-indigo-700 dark:text-indigo-300"
            >
              Download PDF
            </button>
            {allowRegisterExport ? (
              <button
                type="button"
                onClick={() => void handleRegister()}
                disabled={!pdfBlob || rendering || registering}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {registering ? "Registering…" : "Save export"}
              </button>
            ) : null}
          </div>
        </header>

        <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-zinc-950">
          {rendering ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
              Rendering PDF…
            </div>
          ) : showHtmlPreview ? (
            <div className="h-full overflow-y-auto p-4">
              <DocumentBlockReadOnlyList
                blocks={blocks}
                assets={assets}
                variableValues={filledValues}
              />
            </div>
          ) : pdfUrl ? (
            <iframe key={pdfUrl} src={pdfUrl} title="Document PDF preview" className="h-full w-full border-0" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
              PDF preview unavailable.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
